/**
 * **Qué acciones se le ofrecen a una persona sobre un expediente.**
 *
 * ── POR QUÉ VIVE ACÁ Y NO EN EL COMPONENTE ─────────────────────────────────
 *
 * Es el gemelo web de `assertCanTransition`: la API decide si una transición se PUEDE, y esto
 * decide si se OFRECE. Las dos respuestas tienen que coincidir — un botón que la API va a rechazar
 * es una promesa rota, y una transición legítima que no se ofrece es trabajo que alguien hace por
 * otro lado o no hace.
 *
 * Hasta el 2026-08-09 esta decisión vivía dentro del panel, mezclada con etiquetas, íconos y
 * handlers, y **no había una sola prueba sobre ella**: el runner de la web corre en `node` y no
 * monta componentes. Sacarla acá la vuelve verificable sin sumar jsdom, que la config del runner
 * declara como una decisión aparte.
 *
 * ── LO QUE NO HACE ─────────────────────────────────────────────────────────
 *
 * No arma etiquetas ni decide el `variant`: eso es copy y estilo, y sigue en el panel. Acá está
 * sólo la regla — y por eso el retorno son CLAVES, en el orden en que se muestran.
 */

/** Las acciones del panel del expediente, en el orden en que aparecen. */
export const NegotiationAction = {
  Open: 'open',
  Edit: 'edit',
  CopyLink: 'copy-link',
  Advance: 'advance',
  Reject: 'reject',
  Activate: 'activate',
  Inactivate: 'inactivate',
  Rescind: 'rescind',
  Credit: 'credit'
} as const

export type NegotiationAction = (typeof NegotiationAction)[keyof typeof NegotiationAction]

/**
 * Los tipos de estado que mandan sobre la decisión. Se nombran como strings y no se importa el
 * catálogo del dominio a propósito: esto tiene que poder probarse sin arrastrar media app, y el
 * único contrato real es el valor que viaja en `currentState.state.type`.
 */
export type NegotiationActionsInput = {
  /** `currentState.state.type`. */
  currentType: string | null | undefined
  /**
   * Si la API dice que quien pregunta puede AVANZAR este expediente.
   *
   * Hasta el 2026-08-06 acá llegaba `targetStateId` y esta función preguntaba
   * `can.executeState(targetStateId)`. Las dos mitades estaban mal:
   *
   * 1. **El destino lo resolvía el panel** recorriendo `workflow.states` con `order + 1`, y los
   *    `order` de un flujo tienen huecos por diseño — ahí se quedaba sin destino y escondía el
   *    botón. La API ya lo resolvía bien; ahora lo publica.
   * 2. **El permiso se comprobaba a medias**: sólo `Execute` sobre el destino, cuando la API
   *    exige además poder LEERLO. Se ofrecía un botón que el servidor rechaza con 403.
   *
   * Preguntárselo a la API es lo único que hace que las dos respuestas no puedan divergir: es
   * el MISMO predicado que decide, no una copia que hay que mantener sincronizada.
   */
  canAdvance: boolean
  /**
   * Si la API dice que puede DEVOLVER al paso anterior.
   *
   * Antes acá se miraba `currentOrder > 1` y **ningún permiso** — la condición del paso previo
   * sin la de quién puede usarlo.
   */
  canReject: boolean
  inactiveStateId: string | null | undefined
  rescindStateId: string | null | undefined
  can: {
    updateNegotiation: boolean
    activateNegotiation: boolean
    rescindApproved: boolean
    /** `ExecuteStateTransition` sobre un estado concreto. */
    executeState: (stateId: string) => boolean
  }
}

const INACTIVE = 'inactive'
const END = 'end'
const RESCIND = 'rescind'

export function resolveNegotiationActions(input: NegotiationActionsInput): NegotiationAction[] {
  const { currentType, can } = input

  const isInactive = currentType === INACTIVE
  const isApproved = currentType === END
  const isRescinded = currentType === RESCIND
  // Una venta cerrada y una rescindida están TERMINADAS: no se editan ni se mueven.
  const isTerminal = isApproved || isRescinded
  const enCurso = !isInactive && !isTerminal

  const actions: NegotiationAction[] = [NegotiationAction.Open]

  if (can.updateNegotiation && enCurso) actions.push(NegotiationAction.Edit)

  actions.push(NegotiationAction.CopyLink)

  // El `enCurso` sigue acá y no en la API a propósito: es una regla de PRESENTACIÓN —sobre un
  // expediente terminado no se ofrecen transiciones aunque la matriz las permita— y depende sólo
  // del tipo de estado, que la pantalla ya tiene. Lo que se delegó es lo que la pantalla no puede
  // saber: a dónde iría y si quien mira puede llevarlo.
  if (enCurso && input.canAdvance) actions.push(NegotiationAction.Advance)

  if (enCurso && input.canReject) actions.push(NegotiationAction.Reject)

  if (isInactive) {
    /*
     * ☠️ ACÁ HABÍA UN BOTÓN QUE SÓLO PODÍA FALLAR.
     *
     * La versión del panel era `if (isInactive && puedeActivar) … else if (!isTerminal && …)`, y
     * ese `else if` **no comprobaba `!isInactive`**. Entonces a quien estuviera mirando un
     * expediente YA INACTIVO y no tuviera permiso para activarlo —pero sí `Execute` sobre el
     * estado inactivo— se le ofrecía «Inactivar negociación». Apretarlo sólo podía traer el 400
     * `NEGOTIATION_ALREADY_INACTIVE` de la API.
     *
     * No es una regla de negocio que se cambia: es una rama que ninguna respuesta del servidor
     * puede satisfacer. Se corrige separando los dos casos — sobre un expediente inactivo la
     * única acción de encendido/apagado posible es activarlo, y si no se tiene el permiso, ninguna.
     */
    if (can.activateNegotiation) actions.push(NegotiationAction.Activate)
  } else if (!isTerminal && input.inactiveStateId && can.executeState(input.inactiveStateId)) {
    actions.push(NegotiationAction.Inactivate)
  }

  /*
   * Rescindir una venta ya APROBADA es deshacer una venta cerrada y pide su propio permiso —la
   * misma rama que la API, donde ese permiso REEMPLAZA la validación del destino (decisión Nº 10)—.
   * Desde cualquier otro estado alcanza con poder ejecutar hacia RESCINDIDO.
   */
  const puedeRescindir = isApproved
    ? can.rescindApproved
    : Boolean(input.rescindStateId && can.executeState(input.rescindStateId))
  if (!isInactive && !isRescinded && puedeRescindir) actions.push(NegotiationAction.Rescind)

  if (isApproved) actions.push(NegotiationAction.Credit)

  return actions
}
